import React from "react";
import * as translator from "../../../../../utils/translator";
import { MyAccountStoreHowItWorksStep } from "../types";

interface Props {
  language: string;
  titleTranslationKey: string;
  steps: MyAccountStoreHowItWorksStep[];
}

/**
 * "3 einfache Schritte. Komplett online." — vertical stepper (Figma
 * 31687-109432). Each step shows a circular image on the left connected to the
 * next by a vertical green gradient line with a downward arrow, with the step
 * title + description on the right.
 */
const MyAccountStoreHowItWorks: React.FC<Props> = ({ language, titleTranslationKey, steps }) => {
  if (!steps || steps.length === 0) return null;

  const title = translator.translate(language, "my_account", titleTranslationKey);

  return (
    <section className="mb-8">
      <h2 className="text-xl md:text-2xl font-bold text-[#16315c] mb-8">{title}</h2>

      <div className="flex flex-col">
        {steps.map((step, idx) => {
          const stepTitle = translator.translate(language, "my_account", step.titleTranslationKey);
          const stepDescription = translator.translate(language, "my_account", step.descriptionTranslationKey);
          const isLast = idx === steps.length - 1;
          return (
            <div key={idx} className="flex gap-4 md:gap-[15px]">
              {/* Circle + connector column */}
              <div className="flex flex-col items-center shrink-0">
                {/* The PNGs already contain the circular frame + soft shadow
                    (exported from Figma), so we render them as-is rather than
                    wrapping in another circle. */}
                <div className="w-[120px] h-[120px] md:w-[132px] md:h-[132px] shrink-0 flex items-center justify-center">
                  {step.iconSrc && (
                    <img
                      src={step.iconSrc}
                      alt=""
                      className="w-full h-full object-contain"
                      loading="lazy"
                      onError={(e) => {
                        (e.currentTarget.style.display = "none");
                      }}
                    />
                  )}
                </div>
                {/* Vertical green gradient line running from the image above
                    down to the image below, ending in a downward arrowhead
                    (Figma node 31687:109470). */}
                {!isLast && (
                  <div className="flex-1 flex flex-col items-center justify-center py-2">
                    <div className="flex-1 min-h-[28px] w-[2px]" style={{ backgroundColor: "#53AA60" }} />
                    <div
                      className="w-0 h-0"
                      style={{
                        borderLeft: "4px solid transparent",
                        borderRight: "4px solid transparent",
                        borderTop: "6px solid #53AA60",
                      }}
                    />
                  </div>
                )}
              </div>

              {/* Text column */}
              <div className={isLast ? "" : "pb-8"}>
                <h3 className="text-lg md:text-xl font-bold text-[#16315c] mb-1.5">{stepTitle}</h3>
                <p className="text-[15px] md:text-[18px] text-gray-500 leading-relaxed max-w-[491px]">
                  {stepDescription}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default MyAccountStoreHowItWorks;
